import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSeededRandomNumber } from "../../utils/randomUtils"; // Funkce pro generování náhodného čísla s ohledem na seed
import styles from "./Teams.module.scss"; // Import stylů pro tuto komponentu

const Teams = () => {
  const navigate = useNavigate(); // Navigace mezi stránkami
  const [items, setItems] = useState([]); // Seznam položek, které budou rozděleny do týmů
  const [newItem, setNewItem] = useState(""); // Nová položka, která je aktuálně zadávána
  const [mode, setMode] = useState("teams"); // Režim pro generování týmů (buď "teams" nebo "size")
  const [numTeams, setNumTeams] = useState(2); // Počet týmů, pokud je režim "teams"
  const [teamSize, setTeamSize] = useState(2); // Počet lidí v týmu, pokud je režim "size"
  const [teams, setTeams] = useState([]); // Seznam vytvořených týmů
  const [history, setHistory] = useState([]); // Historie posledních rozdělení týmů

  // Funkce pro přidání položky do seznamu
  const addItem = () => {
    if (newItem.trim() !== "") {
      setItems([...items, newItem.trim()]); // Přidá položku, pokud není prázdná
      setNewItem(""); // Vyprázdní vstupní pole po přidání položky
    }
  };

  // Funkce pro odstranění položky ze seznamu
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index)); // Filtruje položky podle indexu
  };

  // Funkce pro přidání položky pomocí klávesy Enter
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Zabrání defaultnímu chování (odeslání formuláře)
      addItem(); // Přidá položku
    }
  };

  // Funkce pro promíchání pole položek
  const shuffleArray = async (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = await getSeededRandomNumber(i + 1); // Použije seedované náhodné číslo
      [arr[i], arr[j]] = [arr[j], arr[i]]; // Vymění položky
    }
    return arr; // Vrátí promíchané pole
  };

  // Funkce pro generování týmů na základě zadaných parametrů
  const handleGenerateTeams = async () => {
    if (items.length === 0) return; // Pokud nejsou položky, nic neprovádí
    let result = [];
    const shuffled = await shuffleArray(items); // Promíchá položky

    if (mode === "teams") {
      let nTeams = parseInt(numTeams, 10); // Počet týmů
      if (isNaN(nTeams) || nTeams < 1) nTeams = 1; // Ošetření neplatného čísla
      if (nTeams > 100) nTeams = 100; // Ošetření maximálního počtu týmů
      if (nTeams > items.length) nTeams = items.length; // Omezí počet týmů na počet položek

      result = Array.from({ length: nTeams }, () => []); // Vytvoří prázdná pole pro týmy
      let index = 0;
      for (let i = 0; i < shuffled.length; i++) {
        result[index].push(shuffled[i]); // Přiřazuje položky do týmů
        index = (index + 1) % nTeams; // Udržuje index v rámci počtu týmů
      }
    } else {
      let size = parseInt(teamSize, 10); // Velikost týmu
      if (isNaN(size) || size < 1) size = 1; // Ošetření neplatného čísla
      if (size > 100) size = 100; // Ošetření maximální velikosti týmu

      const nTeams = Math.ceil(shuffled.length / size); // Počet týmů na základě velikosti
      result = Array.from({ length: nTeams }, () => []); // Vytvoří prázdná pole pro týmy
      let index = 0;
      for (let i = 0; i < shuffled.length; i++) {
        result[index].push(shuffled[i]); // Přiřazuje položky do týmů
        if (result[index].length >= size) {
          index++; // Přesune na další tým, pokud je dosažena maximální velikost
        }
      }
    }

    setTeams(result); // Nastaví vytvořené týmy
    // Uloží historii rozdělení týmů
    const historyEntry = result
      .map((team, idx) => `Tým ${idx + 1}: ${team.join(", ")}`)
      .join(" | ");
    setHistory((prev) => [historyEntry, ...prev].slice(0, 10)); // Přidá novou historii a omezuje na 10 posledních
  };

  return (
    <div className={styles.container}>
      <div className={styles.menu}>
        <button className={styles.backButton} onClick={() => navigate("/")}>
          Zpět
        </button>
        <h3>Týmy</h3>
      </div>
      <div className={styles.content}>
        {/* Režim výběru: "teams" nebo "size" */}
        <div className={styles.modeSelector}>
          <label>
            <input
              type="radio"
              value="teams"
              checked={mode === "teams"}
              onChange={(e) => setMode(e.target.value)}
            />
            Na X týmů
          </label>
          <label>
            <input
              type="radio"
              value="size"
              checked={mode === "size"}
              onChange={(e) => setMode(e.target.value)}
            />
            Po X lidech v týmu
          </label>
        </div>

        {/* Pokud je režim "teams", zobrazí se input pro počet týmů */}
        {mode === "teams" ? (
          <div className={styles.row}>
            <label htmlFor="numTeams">Počet týmů:</label>
            <input
              id="numTeams"
              type="number"
              value={numTeams}
              onChange={(e) =>
                setNumTeams(
                  Math.max(
                    1,
                    Math.min(parseInt(e.target.value, 10) || 1, items.length)
                  )
                )
              }
              min="1"
              max={items.length}
              className={styles.input}
            />
          </div>
        ) : (
          <div className={styles.row}>
            {/* Pokud je režim "size", zobrazí se input pro počet lidí v týmu */}
            <label htmlFor="teamSize">Počet lidí v týmu:</label>
            <input
              id="teamSize"
              type="number"
              value={teamSize}
              onChange={(e) =>
                setTeamSize(Math.max(1, parseInt(e.target.value, 10) || 1))
              }
              min="1"
              max="100"
              className={styles.input}
            />
          </div>
        )}

        {/* Input pro přidání položky */}
        <div className={styles.inputRow}>
          <input
            type="text"
            placeholder="Zadejte jméno/pojem a stiskněte Enter"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyPress={handleKeyPress}
            className={styles.input}
          />
          <button onClick={addItem} className={styles.addButton}>
            Přidat
          </button>
        </div>

        {/* Seznam položek, které byly přidány */}
        {items.length > 0 && (
          <div className={styles.itemList}>
            {items.map((item, index) => (
              <div key={index} className={styles.item}>
                <span>{item}</span>
                <button
                  className={styles.deleteButton}
                  onClick={() => removeItem(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tlačítko pro generování týmů */}
        <button onClick={handleGenerateTeams} className={styles.generateButton}>
          Rozdělit
        </button>

        {/* Zobrazení vygenerovaných týmů */}
        {teams.length > 0 && (
          <div className={styles.teamsContainer}>
            {teams.map((team, i) => (
              <div key={i} className={styles.team}>
                <h4>Tým {i + 1}</h4>
                <ul>
                  {team.map((member, j) => (
                    <li key={j}>{member}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Teams;
